import { createContext, useContext, useState, useEffect } from "react";

/**
 * Preferenza globale di dimensione testo (Piccolo / Medio / Grande).
 * - "small": classe `text-scale-sm` (equivale al vecchio "Medio").
 * - "medium": classe `text-scale-md` (equivale al vecchio "Grande"), default.
 * - "large": classe `text-scale-lg` (ancora piu grande).
 * Persistita in localStorage, come la lingua.
 *
 * SCALE_VERSION: ogni volta che la scala slitta verso l'alto va incrementata
 * e va aggiunta la relativa migrazione, cosi chi aveva gia scelto a mano
 * continua a vedere lo stesso corpo testo invece di ritrovarsi un gradino
 * piu grande senza averlo chiesto.
 */
const FontSizeContext = createContext(null);

const VALID = ["small", "medium", "large"];
const DEFAULT_SCALE = "medium";
const CLASS_BY_SCALE = {
	small: "text-scale-sm",
	medium: "text-scale-md",
	large: "text-scale-lg",
};
const ALL_CLASSES = Object.values(CLASS_BY_SCALE);

const STORAGE_KEY = "fontScale";
const VERSION_KEY = "fontScaleVersion";
const SCALE_VERSION = "2";

/* v1 -> v2: la scala e slittata di un gradino verso l'alto.
   Il valore salvato NON distingue "scelto a mano" da "default scritto al
   primo mount": praticamente tutti hanno "medium" in localStorage senza
   averlo mai toccato, quindi "medium" va portato sul nuovo gradino (e cosi
   anche chi non ha mai aperto le impostazioni vede il testo piu grande).
   Chi aveva scelto "large" finisce sul nuovo "medium", che rende esattamente
   la stessa dimensione di prima; "small" sale di un gradino perche il vecchio
   corpo base era troppo piccolo. "normal" e una label pre-v1 ancora in giro
   su storage molto vecchi. */
const MIGRATION_V1_TO_V2 = {
	small: "small",
	normal: "medium",
	medium: "medium",
	large: "medium",
};

function readInitialScale() {
	const saved = localStorage.getItem(STORAGE_KEY);
	if (!saved) return DEFAULT_SCALE;

	if (localStorage.getItem(VERSION_KEY) !== SCALE_VERSION) {
		return MIGRATION_V1_TO_V2[saved] || DEFAULT_SCALE;
	}

	return VALID.includes(saved) ? saved : DEFAULT_SCALE;
}

export function FontSizeProvider({ children }) {
	const [fontScale, setFontScaleState] = useState(readInitialScale);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, fontScale);
		localStorage.setItem(VERSION_KEY, SCALE_VERSION);
		const root = document.documentElement;
		root.classList.remove(...ALL_CLASSES);
		root.classList.add(CLASS_BY_SCALE[fontScale]);
	}, [fontScale]);

	const setFontScale = (value) => {
		if (VALID.includes(value)) setFontScaleState(value);
	};

	const toggleFontScale = () => {
		setFontScaleState((prev) => {
			if (prev === "small") return "medium";
			if (prev === "medium") return "large";
			return "small";
		});
	};

	return (
		<FontSizeContext.Provider
			value={{ fontScale, setFontScale, toggleFontScale }}
		>
			{children}
		</FontSizeContext.Provider>
	);
}

export function useFontSize() {
	const context = useContext(FontSizeContext);
	if (!context) {
		throw new Error("useFontSize must be used within FontSizeProvider");
	}
	return context;
}
